import React, { useEffect, useMemo, useRef } from 'react';
import { useTheme } from '@mui/material/styles';

const PERSIAN_DIGITS = '\u06F0\u06F1\u06F2\u06F3\u06F4\u06F5\u06F6\u06F7\u06F8\u06F9';
const ARABIC_DIGITS = '\u0660\u0661\u0662\u0663\u0664\u0665\u0666\u0667\u0668\u0669';

const normalizeDigits = (value) =>
  String(value || '')
    .replace(/[\u06F0-\u06F9]/g, (digit) => String(PERSIAN_DIGITS.indexOf(digit)))
    .replace(/[\u0660-\u0669]/g, (digit) => String(ARABIC_DIGITS.indexOf(digit)))
    .replace(/\D/g, '');

const OtpInput = ({
  inputWrapperClass,
  disabled,
  otpLength = 6,
  onChange,
  value = '',
  autocomplete,
}) => {
  const theme = useTheme();
  const inputRefs = useRef([]);
  const pendingFocusIndex = useRef(null);
  const digits = useMemo(
    () => normalizeDigits(value).slice(0, otpLength).split(''),
    [otpLength, value]
  );

  const runAfterRender = (callback) => {
    if (typeof window !== 'undefined' && window.requestAnimationFrame) {
      window.requestAnimationFrame(callback);
      return;
    }

    setTimeout(callback, 0);
  };

  const focusInput = (index) => {
    const safeIndex = Math.max(0, Math.min(index, otpLength - 1));

    runAfterRender(() => {
      const input = inputRefs.current[safeIndex];
      if (!input || input.disabled) return;

      input.focus();
      input.select();
    });
  };

  const queueFocus = (index) => {
    pendingFocusIndex.current = Math.max(0, Math.min(index, otpLength - 1));
    focusInput(pendingFocusIndex.current);
  };

  useEffect(() => {
    if (disabled) return;

    queueFocus(Math.min(digits.length, otpLength - 1));
  }, [disabled]);

  useEffect(() => {
    if (pendingFocusIndex.current === null || disabled) return;

    const nextFocusIndex = pendingFocusIndex.current;
    pendingFocusIndex.current = null;
    focusInput(nextFocusIndex);
  }, [disabled, value]);

  const emitChange = (nextDigits) => {
    onChange?.(nextDigits.join(''));
  };

  const setDigit = (index, digit) => {
    const nextDigits = Array.from({ length: otpLength }, (_, i) => digits[i] || '');
    nextDigits[index] = digit;
    emitChange(nextDigits);
  };

  const fillDigits = (startIndex, pastedValue) => {
    const pastedDigits = normalizeDigits(pastedValue).slice(0, otpLength - startIndex);
    if (!pastedDigits) return;

    const nextDigits = Array.from({ length: otpLength }, (_, i) => digits[i] || '');
    for (let i = 0; i < pastedDigits.length; i += 1) {
      nextDigits[startIndex + i] = pastedDigits[i];
    }

    emitChange(nextDigits);
    queueFocus(Math.min(startIndex + pastedDigits.length, otpLength - 1));
  };

  const handleChange = (event, index) => {
    const enteredDigits = normalizeDigits(event.target.value);
    if (enteredDigits.length > 1) {
      fillDigits(index, enteredDigits);
      return;
    }

    const digit = enteredDigits[enteredDigits.length - 1] || '';
    setDigit(index, digit);

    if (digit && index < otpLength - 1) {
      queueFocus(index + 1);
    }
  };

  const handleKeyDown = (event, index) => {
    if (event.key === 'ArrowLeft' && index > 0) {
      event.preventDefault();
      queueFocus(index - 1);
      return;
    }

    if (event.key === 'ArrowRight' && index < otpLength - 1) {
      event.preventDefault();
      queueFocus(index + 1);
      return;
    }

    if (event.key === 'Backspace') {
      event.preventDefault();
      if (digits[index]) {
        setDigit(index, '');
      } else if (index > 0) {
        setDigit(index - 1, '');
        queueFocus(index - 1);
      }
      return;
    }

    if (event.key === 'Delete') {
      event.preventDefault();
      setDigit(index, '');
      return;
    }

    if (event.key === ' ') {
      event.preventDefault();
    }
  };

  const handlePaste = (event, index) => {
    event.preventDefault();
    fillDigits(index, event.clipboardData.getData('text/plain'));
  };

  return (
    <>
      {Array.from({ length: otpLength }, (_, index) => (
        <input
          key={index}
          ref={(element) => {
            inputRefs.current[index] = element;
          }}
          className={inputWrapperClass}
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          autoFocus={index === 0}
          autoComplete={index === 0 ? autocomplete : undefined}
          disabled={disabled}
          value={digits[index] || ''}
          onChange={(event) => handleChange(event, index)}
          onKeyDown={(event) => handleKeyDown(event, index)}
          onPaste={(event) => handlePaste(event, index)}
          onFocus={(event) => event.target.select()}
          aria-label={`OTP digit ${index + 1}`}
          style={{
            textAlign: 'center',
            border: `1.5px solid ${theme.palette.divider}`,
            borderRadius: 10,
            outline: 'none',
            fontSize: 20,
            fontWeight: 600,
            color: theme.palette.text.primary,
            backgroundColor: theme.palette.background.paper,
            transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
          }}
        />
      ))}
    </>
  );
};

export default OtpInput;
